import { Routes, Route } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Loader2, Shield } from "lucide-react";
import { Layout } from "./components/Layout";
import { VerifierLayout } from "./components/VerifierLayout";
import { AuthGuard } from "./components/AuthGuard";

// Eager load: Landing + auth pages (critical path)
import { Landing } from "./pages/Landing";
import { SignIn } from "./pages/SignIn";
import { Login } from "./pages/Login";
import { SignUp } from "./pages/SignUp";

// Lazy load: All other pages (code splitting)
const Dashboard = lazy(() => import("./pages/Dashboard").then(m => ({ default: m.Dashboard })));
const Credentials = lazy(() => import("./pages/Credentials").then(m => ({ default: m.Credentials })));
const IssueCredential = lazy(() => import("./pages/IssueCredential").then(m => ({ default: m.IssueCredential })));
const BulkIssueCredentials = lazy(() => import("./pages/BulkIssueCredentials").then(m => ({ default: m.BulkIssueCredentials })));
const ClaimCredential = lazy(() => import("./pages/ClaimCredential").then(m => ({ default: m.ClaimCredential })));
const SubjectWallet = lazy(() => import("./pages/SubjectWallet").then(m => ({ default: m.SubjectWallet })));
const SubjectConnect = lazy(() => import("./pages/SubjectConnect").then(m => ({ default: m.SubjectConnect })));
const SubjectProfile = lazy(() => import("./pages/SubjectProfile").then(m => ({ default: m.SubjectProfile })));
const AccountRecovery = lazy(() => import("./pages/AccountRecovery").then(m => ({ default: m.AccountRecovery })));
const GenerateProof = lazy(() => import("./pages/GenerateProof").then(m => ({ default: m.GenerateProof })));
const ImportWallet = lazy(() => import("./pages/ImportWallet").then(m => ({ default: m.ImportWallet })));
const Verify = lazy(() => import("./pages/Verify").then(m => ({ default: m.Verify })));
const CreateVerificationRequest = lazy(() => import("./pages/CreateVerificationRequest").then(m => ({ default: m.CreateVerificationRequest })));
const RespondToVerificationRequest = lazy(() => import("./pages/RespondToVerificationRequest").then(m => ({ default: m.RespondToVerificationRequest })));
const VerifierDashboard = lazy(() => import("./pages/VerifierDashboard").then(m => ({ default: m.VerifierDashboard })));
const VerificationRequestHistory = lazy(() => import("./pages/VerificationRequestHistory").then(m => ({ default: m.VerificationRequestHistory })));
const VerifierIntegration = lazy(() => import("./pages/VerifierIntegration").then(m => ({ default: m.VerifierIntegration })));
const AuditLog = lazy(() => import("./pages/AuditLog").then(m => ({ default: m.AuditLog })));
const Settings = lazy(() => import("./pages/Settings").then(m => ({ default: m.Settings })));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));
const ApiKeys = lazy(() => import("./pages/ApiKeys").then(m => ({ default: m.ApiKeys })));
const Pricing = lazy(() => import("./pages/Pricing").then(m => ({ default: m.Pricing })));
const Documentation = lazy(() => import("./pages/Documentation"));
const About = lazy(() => import("./pages/About").then(m => ({ default: m.About })));
const Contact = lazy(() => import("./pages/Contact").then(m => ({ default: m.Contact })));
const Blog = lazy(() => import("./pages/Blog").then(m => ({ default: m.Blog })));
const Careers = lazy(() => import("./pages/Careers").then(m => ({ default: m.Careers })));
const Privacy = lazy(() => import("./pages/Privacy").then(m => ({ default: m.Privacy })));
const Terms = lazy(() => import("./pages/Terms").then(m => ({ default: m.Terms })));
const Security = lazy(() => import("./pages/Security").then(m => ({ default: m.Security })));
const Compliance = lazy(() => import("./pages/Compliance").then(m => ({ default: m.Compliance })));
const NotFound = lazy(() => import("./pages/NotFound").then(m => ({ default: m.NotFound })));

// Plugin Studio pages
const PluginStudio = lazy(() => import("./pages/PluginStudio").then(m => ({ default: m.PluginStudio })));
const CreatePlugin = lazy(() => import("./pages/CreatePlugin").then(m => ({ default: m.CreatePlugin })));
const PluginDetail = lazy(() => import("./pages/PluginDetail").then(m => ({ default: m.PluginDetail })));
const MyPlugins = lazy(() => import("./pages/MyPlugins").then(m => ({ default: m.MyPlugins })));

/**
 * Loading fallback for lazy-loaded pages
 */
function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="text-center">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-success-500 mb-3">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading...</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Protected Layout wrapper - requires authentication
 */
function ProtectedLayout() {
  return (
    <AuthGuard>
      <Layout />
    </AuthGuard>
  );
}

/**
 * Protected Verifier Layout wrapper - requires authentication
 */
function ProtectedVerifierLayout() {
  return (
    <AuthGuard>
      <VerifierLayout />
    </AuthGuard>
  );
}

function App() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<Landing />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/docs" element={<Documentation />} />
      <Route path="/about" element={<About />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/careers" element={<Careers />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/security" element={<Security />} />
      <Route path="/compliance" element={<Compliance />} />
      
      {/* Public credential claim route - for subjects to claim their credentials */}
      <Route path="/claim/:token" element={<ClaimCredential />} />
      <Route path="/accept-invite/:token" element={<AcceptInvite />} />
      
      {/* Public verification request route - for subjects to respond to requests */}
      <Route path="/verification-request/:token" element={<RespondToVerificationRequest />} />
      
      {/* Subject wallet routes - public, no auth required (uses local storage) */}
      <Route path="/connect" element={<SubjectConnect />} />
      <Route path="/recovery" element={<AccountRecovery />} />
      <Route path="/wallet" element={<SubjectWallet />} />
      <Route path="/wallet/profile" element={<SubjectProfile />} />
      <Route path="/wallet/proof/:credentialId" element={<GenerateProof />} />
      <Route path="/wallet/import" element={<ImportWallet />} />
      
      {/* Authenticated app routes */}
      <Route path="/dashboard" element={<ProtectedLayout />}>
        <Route index element={<Dashboard />} />
      </Route>
      <Route path="/credentials" element={<ProtectedLayout />}>
        <Route index element={<Credentials />} />
        <Route path="issue" element={<IssueCredential />} />
        <Route path="bulk" element={<BulkIssueCredentials />} />
      </Route>
      <Route path="/verify" element={<ProtectedLayout />}>
        <Route index element={<Verify />} />
        <Route path="request" element={<CreateVerificationRequest />} />
      </Route>
      <Route path="/audit" element={<ProtectedLayout />}>
        <Route index element={<AuditLog />} />
      </Route>
      <Route path="/settings" element={<ProtectedLayout />}>
        <Route index element={<Settings />} />
      </Route>
      <Route path="/api-keys" element={<ProtectedLayout />}>
        <Route index element={<ApiKeys />} />
      </Route>
      
      {/* Plugin Studio routes */}
      <Route path="/studio" element={<ProtectedLayout />}>
        <Route index element={<PluginStudio />} />
        <Route path="create" element={<CreatePlugin />} />
        <Route path="marketplace" element={<PluginStudio />} />
        <Route path="my-plugins" element={<MyPlugins />} />
        <Route path="plugins/:id" element={<PluginDetail />} />
      </Route>
      
      {/* Verifier-specific routes */}
      <Route path="/verifier" element={<ProtectedVerifierLayout />}>
        <Route index element={<VerifierDashboard />} />
        <Route path="requests" element={<VerificationRequestHistory />} />
        <Route path="integration" element={<VerifierIntegration />} />
      </Route>
      
      {/* 404 Not Found */}
      <Route path="*" element={<NotFound />} />
    </Routes>
    </Suspense>
  );
}

export default App;
